import { useEffect, useState } from 'react';
import { Card, Heading, Container, VStack, Input, Textarea, Button } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import ContentTypeActions from './actions/content-type-actions';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  sport_type_id: z.number().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function EditContentLibrary() {
  const navigate = useNavigate();
  const { loadContentTypes } = useContentTypesStore();
  const [isBaseContentType, setIsBaseContentType] = useState<boolean>(true);
  const { workspaceId, id } = useParams<{
    workspaceId: string;
    id: string;
  }>();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    if (!workspaceId || !id) {
      return;
    }

    try {
      await api.put(`content-types/${id}`, {
        name: data.name,
        description: data.description,
        workspace_id: parseInt(workspaceId),
        sport_type_id: data.sport_type_id,
      });
      if (workspaceId) {
        loadContentTypes(workspaceId);
      }
      navigate(`/${workspaceId}/content-types`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update content library',
      });
    }
  };

  useEffect(() => {
    async function fetchField() {
      const response = await api.get(`content-types/${id}`);
      if (response.data.data) {
        const contentType = response.data.data;
        if (workspaceId) {
          setIsBaseContentType(
            parseInt(contentType.workspace_id, 10) !== parseInt(workspaceId, 10),
          );
        }

        reset({
          name: contentType.name,
          description: contentType.description,
          sport_type_id: contentType.sport_type_id,
        });
      }
    }
    fetchField();
  }, [id, workspaceId, reset]);

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Heading size='3xl'>Edit custom library</Heading>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form id='edit-content-library' onSubmit={handleSubmit(onSubmit)}>
              <VStack align={'stretch'} gap={4}>
                <Field
                  label='Name'
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                  disabled={isBaseContentType}
                >
                  <Input {...register('name')} />
                </Field>
                <Field label='Description' disabled={isBaseContentType}>
                  <Textarea {...register('description')} rows={4} />
                </Field>
                {workspaceId && id && (
                  <ContentTypeActions workspaceId={workspaceId} contentTypeId={id} />
                )}
                {!isBaseContentType && (
                  <Button
                    colorPalette='brand'
                    form='edit-content-library'
                    type='submit'
                    width='full'
                    mt={5}
                    loading={isSubmitting}
                    loadingText='Loading...'
                  >
                    Save
                  </Button>
                )}
              </VStack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
